import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, Calendar } from 'lucide-react';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';
import { SERVICES, CONTACT } from '../../site/nav';
import { headFor } from '../../site/heads';

// One layout for every service page. The copy comes in as data so each
// page stays short and reads the same way: what it is, what it does, what
// the customer gets, how it works, questions, and a way to book.
//
//   eyebrow     small label above the title
//   title       the h1, plain text (a <br /> can split it)
//   lead        one or two sentences under the title
//   proof       optional { label, href } link under the lead
//   features    [{ title, text }]  what it does, 4 to 6 items
//   customer    { title, items: [{ title, text }] }  what the shopper gets
//   steps       [{ title, text }]  how it starts, 3 items
//   faqs        [[question, answer]]
//   closing     { title, text }  the last word before the book button
//   children    extra sections placed right after the hero
//   after       extra sections placed after the closing (a Sources list)
export default function ServicePage({ path, eyebrow, title, lead, proof, features = [], customer, steps = [], faqs = [], closing, children, after }) {
  const head = headFor(path);
  useEffect(() => { document.title = head.title; }, [head.title]);
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [hash]);
  const [openFaq, setOpenFaq] = useState(null);
  const others = SERVICES.filter((s) => s.path !== path);

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-4 focus:py-2 focus:bg-white focus:text-blue-700 focus:rounded-lg focus:font-semibold">Skip to main content</a>
      <SiteNav />

      <main id="main-content">
        <section className="pt-28 md:pt-40 pb-12 md:pb-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <p className="text-blue-600 font-semibold uppercase tracking-[0.28em] text-[10px] md:text-xs mb-4 md:mb-5">{eyebrow}</p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] mb-5 md:mb-6">{title}</h1>
            <p className="text-base md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-6 md:mb-8">{lead}</p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <a href={CONTACT.calendlyUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-semibold hover:shadow-lg hover:shadow-blue-600/25 transition-all duration-300">
                <Calendar className="w-4 h-4" />
                <span>Book a 30 minute demo</span>
              </a>
              {proof && (
                <a href={proof.href} target={proof.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="group inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-3.5 bg-white border border-blue-600 text-blue-700 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300">
                  <span>{proof.label}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              )}
            </div>
          </div>
        </section>

        {children}

        {features.length > 0 && (
          <section className="py-12 md:py-20 px-4 border-t border-slate-200">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-center mb-8 md:mb-12">What it does</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {features.map((f) => (
                  <div key={f.title} className="bg-white border border-slate-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] p-5 md:p-7 hover:border-blue-500 transition-all">
                    <h3 className="text-slate-900 text-base md:text-lg font-bold mb-2 tracking-tight">{f.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {customer && (
          <section className="py-12 md:py-20 px-4 border-t border-slate-200 bg-slate-50">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-center mb-3 md:mb-4">{customer.title}</h2>
              {customer.lead && <p className="text-slate-500 text-center max-w-2xl mx-auto mb-8 md:mb-12">{customer.lead}</p>}
              <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                {customer.items.map((c) => (
                  <div key={c.title} className="relative">
                    <div className="absolute -inset-[1.5px] bg-gradient-to-b from-blue-600 to-blue-200 rounded-2xl" />
                    <div className="relative h-full bg-white rounded-2xl p-5 md:p-7">
                      <h3 className="text-slate-900 text-base md:text-lg font-bold mb-2 tracking-tight">{c.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {steps.length > 0 && (
          <section className="py-12 md:py-20 px-4 border-t border-slate-200">
            <div className="container mx-auto max-w-4xl">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-center mb-8 md:mb-12">How it starts</h2>
              <ol className="grid md:grid-cols-3 gap-4 md:gap-6">
                {steps.map((s, i) => (
                  <li key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
                    <p className="text-blue-600 font-bold text-2xl mb-2">{i + 1}</p>
                    <h3 className="text-slate-900 font-bold mb-1.5">{s.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{s.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {faqs.length > 0 && (
          <section className="py-12 md:py-20 px-4 border-t border-slate-200 bg-slate-50">
            <div className="container mx-auto max-w-3xl">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-center mb-8 md:mb-10">Questions</h2>
              <div className="space-y-3">
                {faqs.map(([q, a], i) => (
                  <div key={q} className="rounded-2xl border border-slate-200 bg-white">
                    <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-semibold">
                      <span>{q}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 text-blue-600 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                    </button>
                    {/* Answers stay in the HTML for crawlers; the toggle only hides them. */}
                    <p className={`px-5 pb-5 text-slate-500 text-sm leading-relaxed ${openFaq === i ? '' : 'hidden'}`}>{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {closing && (
          <section className="py-14 md:py-24 px-4 border-t border-slate-200">
            <div className="container mx-auto max-w-3xl text-center">
              <h2 className="text-2xl md:text-4xl font-bold tracking-tight mb-4">{closing.title}</h2>
              <p className="text-slate-600 md:text-lg mb-7 md:mb-8">{closing.text}</p>
              <a href={CONTACT.calendlyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 md:px-9 py-3 md:py-4 bg-blue-600 text-white hover:bg-blue-700 rounded-full font-semibold hover:shadow-lg hover:shadow-blue-600/25 transition-all duration-300 hover:scale-105">
                <Calendar className="w-4 h-4" />
                <span>Book a demo</span>
              </a>
            </div>
          </section>
        )}

        {after}

        <section className="py-10 md:py-14 px-4 border-t border-slate-200">
          <div className="container mx-auto max-w-6xl">
            <p className="text-slate-400 text-[10px] md:text-xs uppercase tracking-[0.28em] text-center mb-5">More from Exitium</p>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {others.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.slug} to={s.path} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm text-slate-600 hover:text-slate-900 hover:border-blue-500 transition-colors">
                    <Icon className="w-4 h-4 text-blue-600" />
                    {s.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
